import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, User, Calendar, Shield, Clock, Target, Upload, Bell, Plus, Search, Eye, FileText, MessageCircle, UserPlus, Menu, X, Building2, Users, Briefcase, TrendingUp, MapPin, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Footer } from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { supabase } from "@/integrations/supabase/client";

export default function Employers() {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const logoContainerRef = useRef<HTMLDivElement>(null);
  const [currentLogoIndex, setCurrentLogoIndex] = useState(0);
  
  // FAQ state
  const [openFAQs, setOpenFAQs] = useState<boolean[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [questionForm, setQuestionForm] = useState({
    name: '',
    contact: '',
    question: ''
  });

  // FAQ data - will be populated from database
  const [faqs, setFaqs] = useState([
    {
      question: "How quickly can you fill a position?",
      answer: "Our average time to fill is less than two weeks. We maintain a pool of pre-screened candidates and use AI matching to ensure rapid placement while maintaining quality."
    },
    {
      question: "What types of healthcare positions do you fill?",
      answer: "We fill all types of healthcare positions including nurses (RN, LPN, CNA), physicians, therapists, technicians, and administrative staff across all specialties and departments."
    },
    {
      question: "How do you ensure candidate quality?",
      answer: "All candidates undergo license verification, skills assessments, and reference checks."
    }
  ]);

  // Filtered FAQs based on search
  const filteredFAQs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Initialize openFAQs array and fetch published FAQs
  useEffect(() => {
    setOpenFAQs(new Array(faqs.length).fill(false));
    
    // Fetch published FAQs from the database
    const fetchPublishedFAQs = async () => {
      try {
        const { data, error } = await supabase
          .from('faq_questions')
          .select('question, answer')
          .eq('status', 'published')
          .order('published_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching FAQs:', error);
          return;
        }
        
        if (data && data.length > 0) {
          // Update FAQs with published questions from database
          const publishedFAQs = data.map(faq => ({
            question: faq.question,
            answer: faq.answer || ''
          }));
          
          // Combine with default FAQs, prioritizing published ones
          const defaultFAQs = [
            {
              question: "How quickly can you fill a position?",
              answer: "Our average time to fill is less than two weeks. We maintain a pool of pre-screened candidates and use AI matching to ensure rapid placement while maintaining quality."
            },
            {
              question: "What types of healthcare positions do you fill?",
              answer: "We fill all types of healthcare positions including nurses (RN, LPN, CNA), physicians, therapists, technicians, and administrative staff across all specialties and departments."
            },
            {
              question: "How do you ensure candidate quality?",
              answer: "All candidates undergo license verification, skills assessments, and reference checks."
            }
          ];
          
          // Merge published FAQs with defaults, avoiding duplicates
          const mergedFAQs = [...publishedFAQs];
          defaultFAQs.forEach(defaultFAQ => {
            if (!mergedFAQs.some(faq => faq.question === defaultFAQ.question)) {
              mergedFAQs.push(defaultFAQ);
            }
          });
          
          setFaqs(mergedFAQs);
          setOpenFAQs(new Array(mergedFAQs.length).fill(false));
        }
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      }
    };
    
    fetchPublishedFAQs();
  }, []);

  // FAQ selection and interaction
  const [selectedFAQ, setSelectedFAQ] = useState<number | null>(null);
  const [showSubmitForm, setShowSubmitForm] = useState(false);

  const selectFAQ = (index: number) => {
    setSelectedFAQ(index);
    setShowSubmitForm(false);
  };

  const showForm = () => {
    setShowSubmitForm(true);
    setSelectedFAQ(null);
  };

  // Handle question submission
  const handleSubmitQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionForm.question.trim()) return;
    
    setIsSubmitting(true);
    
    try {
      console.log('Submitting question:', questionForm.question);
      console.log('Supabase client:', supabase);
      
      // Submit directly to Supabase
      const { data, error } = await supabase
        .from('faq_questions')
        .insert({
          question: questionForm.question.trim(),
          submitter_name: questionForm.name.trim() || null,
          submitter_contact: questionForm.contact.trim() || null,
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        console.error('Supabase error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw new Error(error.message || 'Failed to submit question');
      }

      console.log('Question submitted successfully:', data);
      
      // Reset form
      setQuestionForm({ name: '', contact: '', question: '' });
      
      // Show success message
      alert('Question submitted successfully! We\'ll review and respond soon.');
      
      // Optionally refresh the FAQs if you want to show the new question
      // This would require implementing a way to fetch published FAQs
      
    } catch (error) {
      console.error('Error submitting question:', error);
      alert(`Error submitting question: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  useSEO({
    title: "Healthcare Employers - Nomera Health",
    description: "Find qualified healthcare professionals faster. Access pre-screened candidates and streamline your recruitment process.",
    keywords: "healthcare recruitment, hire nurses, medical staffing, healthcare hiring, talent acquisition, Missouri, Kansas"
  });

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollLogosBy = (direction: 'prev' | 'next') => {
    const container = logoContainerRef.current;
    if (!container) return;
    const amount = container.clientWidth * 0.3; // scroll by ~30% of visible width for smoother movement
    container.scrollBy({ left: direction === 'next' ? amount : -amount, behavior: 'smooth' });
  };

  const handlePrevLogo = () => {
    setCurrentLogoIndex((i) => (i - 1 + 10) % 10);
    scrollLogosBy('prev');
  };

  const handleNextLogo = () => {
    setCurrentLogoIndex((i) => (i + 1) % 10);
    scrollLogosBy('next');
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Ambient background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#9B8FC1]/5 rounded-full blur-3xl animate-pulse-glow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#A3CFA2]/5 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1.5s' }}></div>
      </div>

      {/* Header */}
      <header role="banner" className="sticky top-0 z-50">
        <div
          className={`relative backdrop-blur-md transition-all duration-300 ${
            scrolled
              ? 'shadow-[0_6px_20px_rgba(0,0,0,.06)]'
              : ''
          }`}
          style={{ backdropFilter: 'blur(8px)', backgroundColor: scrolled ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.75)' }}
        >
          <style>{`
            @keyframes nxr-logo-in { from { opacity:0; transform: scale(.98) } to { opacity:1; transform: scale(1) } }
            @keyframes nxr-logo-breathe { 0%,100% { transform: scale(1) } 50% { transform: scale(1.01) } }
            .nxr-logo { animation: nxr-logo-in .6s ease both, nxr-logo-breathe 6s ease-in-out infinite; }
            .nxr-navlink { position: relative; transition: color .2s ease; }
            .nxr-navlink:after { content:''; position:absolute; left:0; right:0; bottom:-4px; height:2px; background: linear-gradient(90deg, rgba(155,143,193,.6), rgba(163,207,162,.6)); transform: scaleX(0); transform-origin:left; transition: transform .25s ease, opacity .25s ease; opacity:0; }
            .nxr-navlink:hover:after, .nxr-navlink:focus-visible:after { transform: scaleX(1); opacity:1; }
            @media(prefers-reduced-motion:reduce){ .nxr-logo { animation:none !important } .nxr-navlink:after { transition: none } }
          `}</style>
          
          <div className="max-w-6xl mx-auto px-6 h-16 md:h-18 flex items-center justify-between text-neutral-800">
            {/* Left: Logo */}
            <Link to="/" className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">
              <img
                src="/lovable-uploads/Favicon.png"
                alt="Nomera Health Logo" 
                className="w-8 h-8 rounded-xl nxr-logo"
              />
              <span className="text-lg font-bold">Nomera Health</span>
            </Link>

            {/* Center: Desktop Nav */}
            <nav className="hidden md:block" aria-label="Main">
              <ul className="flex items-center gap-6">
                <li><Link to="/" className="nxr-navlink hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">Home</Link></li>
                <li><Link to="/healthcare-professionals" className="nxr-navlink hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">For Healthcare Professionals</Link></li>
                <li><Link to="/vision" className="nxr-navlink hover:text-neutral-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">Vision</Link></li>
              </ul>
            </nav>

            {/* Right: Secondary + CTA */}
            <div className="hidden md:flex items-center gap-3">
              <span className="text-neutral-400 font-semibold pointer-events-none select-none">Coming soon</span>
              <Button
                size="sm"
                disabled
                className="rounded-xl bg-[#9B8FC1]/40 text-white/70 font-semibold shadow-none pointer-events-none select-none"
              >
                Coming soon
              </Button>
            </div>

            {/* Mobile: hamburger */}
            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              onClick={() => setIsMenuOpen((v) => !v)}
              className="md:hidden p-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile panel */}
          <div
            id="mobile-menu"
            className={`md:hidden origin-top transition-[max-height,opacity] duration-300 ${isMenuOpen ? 'max-h-[360px] opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}
          >
            <div className="px-6 pb-6 pt-2">
              <div className="rounded-xl border border-black/5 bg-white/80 supports-[backdrop-filter]:backdrop-blur-md p-4">
                <nav aria-label="Main mobile">
                  <ul className="flex flex-col gap-3">
                    <li><Link to="/" onClick={() => setIsMenuOpen(false)} className="py-3 block nxr-navlink focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">Home</Link></li>
                    <li><Link to="/healthcare-professionals" onClick={() => setIsMenuOpen(false)} className="py-3 block nxr-navlink focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">For Healthcare Professionals</Link></li>
                    <li><Link to="/vision" onClick={() => setIsMenuOpen(false)} className="py-3 block nxr-navlink focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40">Vision</Link></li>
                  </ul>
                </nav>
                <div className="mt-4 flex items-center gap-3">
                  <span className="text-neutral-400 font-semibold">Coming soon</span>
                  <Button
                    size="sm"
                    disabled
                    className="w-full rounded-xl bg-[#9B8FC1]/40 text-white/70 font-semibold shadow-none pointer-events-none select-none"
                  >
                    Coming soon
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section
        className="relative min-h-[80vh] flex flex-col justify-center px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, #F8F6FF 0%, #F0F0FF 50%, #FFFFFF 100%)`,
        }}
      >
        <div className="max-w-6xl mx-auto w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Text Content */}
            <div className="max-w-2xl">
              {/* Header */}
              <div className="mb-3">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-tight tracking-tight text-neutral-900">
                  <span className="block text-[#9B8FC1]">Stop struggling with</span>
                  <span className="block">healthcare staffing <span className="text-[#A3CFA2]">shortages.</span></span>
                </h1>
              </div>

              {/* Subheader */}
              <div className="mb-4">
                <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-neutral-600 leading-relaxed">
                  Access a curated pool of pre-screened healthcare professionals and fill critical positions in <strong>less than two weeks</strong> with our AI-powered matching system.
                </p>
              </div>

              {/* Trust Points */}
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 md:gap-6 lg:gap-8">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-[#9B8FC1] opacity-70" />
                    <span className="text-xs sm:text-sm text-neutral-500">AI-powered matching</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-[#9B8FC1] opacity-70" />
                    <span className="text-xs sm:text-sm text-neutral-500">48-hour turnaround</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-[#9B8FC1] opacity-70" />
                    <span className="text-xs sm:text-sm text-neutral-500">95% success rate</span>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 mb-8">
                <Button
                  size="lg"
                  disabled
                  className="bg-[#9B8FC1]/40 text-white/80 font-bold text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4 shadow-none pointer-events-none select-none"
                >
                  Coming soon
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  disabled
                  className="border-2 border-neutral-200 text-neutral-400 font-semibold text-base sm:text-lg px-5 sm:px-6 py-3 sm:py-4 pointer-events-none select-none"
                >
                  Coming soon
                </Button>
              </div>
            </div>

            {/* Right: Employer Image with Artistic Framing */}
            <div className="relative lg:flex justify-center items-center">
              {/* Main Image Container */}
              <div className="relative w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-4xl mx-auto lg:mx-0">
                {/* Abstract Background Shapes */}
                <div className="absolute inset-0 -z-10">
                  {/* Large circular element with stripes */}
                  <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 lg:-bottom-8 lg:-right-8 w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 bg-gradient-to-br from-[#9B8FC1]/20 to-[#A3CFA2]/20 rounded-full opacity-80">
                    <div className="absolute inset-2 bg-white/30 rounded-full"></div>
                    <div className="absolute inset-0 rounded-full" style={{
                      background: `repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(155,143,193,0.3) 4px, rgba(155,143,193,0.3) 8px)`
                    }}></div>
                  </div>
                  
                  {/* Floating diamond shape */}
                  <div className="absolute -top-2 -right-2 sm:-top-3 sm:-right-3 lg:-top-4 lg:-right-4 w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-[#A3CFA2]/30 to-[#9B8FC1]/30 transform rotate-45 opacity-80"></div>
                  
                  {/* Curved abstract shape */}
                  <div className="absolute -bottom-2 -left-2 sm:-bottom-3 sm:-left-3 lg:-bottom-4 lg:-left-4 w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-[#9B8FC1]/20 to-[#A3CFA2]/20 rounded-full opacity-60" style={{
                    clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 70%)'
                  }}></div>
                  
                  {/* Dot pattern */}
                  <div className="absolute -bottom-1 -right-1 sm:-bottom-2 sm:-right-2 lg:-bottom-2 lg:-right-2 w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 opacity-60">
                    <div className="grid grid-cols-4 gap-0.5 sm:gap-1">
                      {Array.from({ length: 16 }).map((_, i) => (
                        <div key={i} className="w-0.5 h-0.5 sm:w-1 sm:h-1 bg-[#A3CFA2] rounded-full opacity-60"></div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Employer Image Container */}
                <div className="relative bg-gradient-to-br from-[#9B8FC1]/10 to-[#A3CFA2]/10 rounded-2xl sm:rounded-3xl p-3 sm:p-4 md:p-5 lg:p-6 shadow-xl lg:shadow-2xl">
                  {/* Speech bubble elements */}
                  <div className="absolute -top-4 -left-2 sm:-top-6 sm:-left-3 lg:-top-8 lg:-left-4 bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl px-2 py-1 sm:px-3 sm:py-2 shadow-lg border border-[#9B8FC1]/20">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-[#9B8FC1]" />
                      <span className="text-xs sm:text-sm font-medium text-[#A3CFA2]">Quality Talent</span>
                    </div>
                  </div>
                  
                  <div className="absolute -top-3 -right-2 sm:-top-5 sm:-right-3 lg:-top-6 lg:-right-4 bg-white/90 backdrop-blur-sm rounded-xl sm:rounded-2xl px-2 py-1 sm:px-3 sm:py-2 shadow-lg border border-[#A3CFA2]/20">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-[#A3CFA2]" />
                      <span className="text-xs sm:text-sm font-medium text-[#9B8FC1]">Fast Hiring</span>
                    </div>
                  </div>

                  {/* Main Employer Image */}
                  <div className="relative">
                    <img
                      src="/materials/nurse-doctor-photo.png"
                      alt="Healthcare team collaboration - showing doctors and nurses working together"
                      className="w-full h-auto rounded-xl sm:rounded-2xl shadow-lg object-cover"
                    />
                    
                    {/* Subtle overlay for depth */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent rounded-xl sm:rounded-2xl"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partner Logos Section */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-base md:text-lg font-medium text-[#36424a] mb-3">
              Open To: <strong className="font-bold">Missouri</strong> • <strong className="font-bold">Kansas</strong>
            </p>
            <p className="text-xs md:text-sm text-[#8a8f9a] italic mb-8 leading-relaxed opacity-80">
              No affiliation with these companies. Logos used for regional reference only.
            </p>
          </div>
          
          <div className="partner-showcase" aria-label="Hospitals and health systems we serve">
            {/* Desktop: Full grid with navigation */}
            <div className="hidden md:flex items-center justify-between">
              <button onClick={() => handlePrevLogo()} className="partner-nav-btn partner-nav-left" aria-label="Previous logo">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <div className="partner-logos-container" ref={logoContainerRef}>
                <div className="partner-logos-grid">
                  <img src="/Logos/01-hca-midwest-health.png" alt="HCA Midwest Health" className="partner-logo" />
                  <img src="/Logos/02-saint-lukes.png" alt="Saint Luke's" className="partner-logo" />
                  <img src="/Logos/03-sunflower-medical-group.png" alt="Sunflower Medical Group" className="partner-logo" />
                  <img src="/Logos/04-childrens-mercy-kansas-city.png" alt="Children's Mercy Kansas City" className="partner-logo" />
                  <img src="/Logos/05-stormont-vail-health.png" alt="Stormont Vail Health" className="partner-logo" />
                  <img src="/Logos/06-saint-joseph-hospital.png" alt="Saint Joseph Hospital" className="partner-logo" />
                  <img src="/Logos/07-clay-platte-family-medicine-clinic.png" alt="Clay Platte Family Medicine Clinic" className="partner-logo" />
                  <img src="/Logos/08-liberty-hospital.png" alt="Liberty Hospital" className="partner-logo" />
                  <img src="/Logos/09-kindred-hospital.png" alt="Kindred Hospital" className="partner-logo" />
                  <img src="/Logos/10-providence-medical-center.png" alt="Providence Medical Center" className="partner-logo" />
                </div>
              </div>
              
              <button onClick={() => handleNextLogo()} className="partner-nav-btn partner-nav-right" aria-label="Next logo">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Mobile: Carousel with visible navigation */}
            <div className="md:hidden">
              <div className="flex items-center justify-between mb-4">
                <button 
                  onClick={() => setCurrentLogoIndex(prev => prev === 0 ? 9 : prev - 1)} 
                  className="partner-nav-btn partner-nav-left" 
                  aria-label="Previous logo"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <div className="text-sm text-neutral-600 font-medium">
                  {currentLogoIndex + 1} of 10
                </div>
                
                <button 
                  onClick={() => setCurrentLogoIndex(prev => prev === 9 ? 0 : prev + 1)} 
                  className="partner-nav-btn partner-nav-right" 
                  aria-label="Next logo"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              
              <div className="partner-logos-container-mobile">
                <div className="partner-logos-carousel" style={{ transform: `translateX(-${currentLogoIndex * 100}%)` }}>
                  <img src="/Logos/01-hca-midwest-health.png" alt="HCA Midwest Health" className="partner-logo-mobile" />
                  <img src="/Logos/02-saint-lukes.png" alt="Saint Luke's" className="partner-logo-mobile" />
                  <img src="/Logos/03-sunflower-medical-group.png" alt="Sunflower Medical Group" className="partner-logo-mobile" />
                  <img src="/Logos/04-childrens-mercy-kansas-city.png" alt="Children's Mercy Kansas City" className="partner-logo-mobile" />
                  <img src="/Logos/05-stormont-vail-health.png" alt="Stormont Vail Health" className="partner-logo-mobile" />
                  <img src="/Logos/06-saint-joseph-hospital.png" alt="Saint Joseph Hospital" className="partner-logo-mobile" />
                  <img src="/Logos/07-clay-platte-family-medicine-clinic.png" alt="Clay Platte Family Medicine Clinic" className="partner-logo-mobile" />
                  <img src="/Logos/08-liberty-hospital.png" alt="Liberty Hospital" className="partner-logo-mobile" />
                  <img src="/Logos/09-kindred-hospital.png" alt="Kindred Hospital" className="partner-logo-mobile" />
                  <img src="/Logos/10-providence-medical-center.png" alt="Providence Medical Center" className="partner-logo-mobile" />
                </div>
              </div>
              
              {/* Mobile dot indicators */}
              <div className="flex justify-center mt-4 space-x-2">
                {Array.from({ length: 10 }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentLogoIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentLogoIndex 
                        ? 'bg-[#9B8FC1] w-4' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to logo ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits for Healthcare Employers Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              Transform Your Healthcare Recruitment
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Join healthcare facilities achieving measurable results with Nomera Health
            </p>
          </div>

          <div className="space-y-20">
            {/* Section 1: Cost Savings */}
            <div className="relative">
              <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-[#9B8FC1] to-[#A3CFA2] rounded-full opacity-20"></div>
              <div className="grid lg:grid-cols-2 gap-20 items-center pl-8">
                {/* Mobile: Image on top, Desktop: Image on right */}
                <div className="flex justify-center lg:hidden mb-12">
                  <img 
                    src="/materials/resource-creative-image.png" 
                    alt="Resource management and cost savings illustration"
                    className="w-80 h-80 md:w-96 md:h-96 object-contain drop-shadow-2xl"
                  />
                </div>
                
                <div className="text-left">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#9B8FC1]/10 text-[#9B8FC1] text-sm font-medium rounded-full mb-6">
                    <span className="w-2 h-2 bg-[#9B8FC1] rounded-full"></span>
                    Cost Optimization
                  </div>
                  <h3 className="text-3xl font-bold text-neutral-900 mb-8">
                    Cut premium labor costs at the source
                  </h3>
                  <div className="space-y-5 text-lg text-neutral-600 leading-relaxed">
                    <p>
                      Replacing one RN now costs facilities an average of <strong className="text-[#9B8FC1]">$61,000</strong> — not just in recruiting, but also in lost productivity, overtime/agency reliance, onboarding, and costly errors from understaffing.
                    </p>
                    <p>
                      With Nomera Health, you bypass those drains. Direct access to actively seeking, pre-vetted candidates means faster backfills, fewer gaps, and dramatically lower labor costs — without the <strong className="text-[#A3CFA2]">15–20% staffing agency markups</strong>.
                    </p>
                  </div>
                </div>
                
                {/* Desktop: Image on right */}
                <div className="hidden lg:flex justify-center">
                  <img 
                    src="/materials/resource-creative-image.png" 
                    alt="Resource management and cost savings illustration"
                    className="w-80 h-80 md:w-96 md:h-96 lg:w-[500px] lg:h-[500px] object-contain drop-shadow-2xl"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Speed */}
            <div className="relative">
              <div className="absolute right-0 top-0 w-1 h-full bg-gradient-to-b from-[#A3CFA2] to-[#9B8FC1] rounded-full opacity-20"></div>
              <div className="grid lg:grid-cols-2 gap-20 items-center pr-8">
                {/* Mobile: Image on top, Desktop: Image on left */}
                <div className="flex justify-center lg:hidden mb-12 order-1">
                  <img 
                    src="/materials/slash-time-image.png" 
                    alt="Time-to-fill optimization illustration"
                    className="w-80 h-80 md:w-96 md:h-96 object-contain drop-shadow-2xl"
                  />
                </div>
                
                {/* Desktop: Image on left */}
                <div className="hidden lg:flex justify-center order-2 lg:order-1">
                  <img 
                    src="/materials/slash-time-image.png" 
                    alt="Time-to-fill optimization illustration"
                    className="w-80 h-80 md:w-96 md:h-96 lg:w-[500px] lg:h-[500px] object-contain drop-shadow-2xl"
                  />
                </div>
                
                <div className="text-left order-2 lg:order-2">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#A3CFA2]/10 text-[#A3CFA2] text-sm font-medium rounded-full mb-6">
                    <span className="w-2 h-2 bg-[#A3CFA2] rounded-full"></span>
                    Speed & Efficiency
                  </div>
                  <h3 className="text-3xl font-bold text-neutral-900 mb-8">
                    Slash time-to-fill from months to days
                  </h3>
                  <div className="space-y-5 text-lg text-neutral-600 leading-relaxed">
                    <p>
                      Nationally, it takes an average of <strong className="text-[#9B8FC1]">49 days</strong> to fill a healthcare role — and for specialized positions, it can drag out to <strong className="text-[#A3CFA2]">250+ days</strong>.
                    </p>
                    <p>
                      With Nomera Health, qualified candidates are automatically matched the moment they're available, cutting vacancy periods dramatically and filling roles at a fraction of the time.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Retention */}
            <div className="relative">
              <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-[#9B8FC1] to-[#A3CFA2] rounded-full opacity-20"></div>
              <div className="grid lg:grid-cols-2 gap-20 items-center pl-8">
                {/* Mobile: Image on top, Desktop: Image on right */}
                <div className="flex justify-center lg:hidden mb-12">
                  <img 
                    src="/materials/cycle-of-mismatch-profile-image.png" 
                    alt="Cycle of mismatched hires illustration"
                    className="w-80 h-80 md:w-96 md:h-96 object-contain drop-shadow-2xl"
                  />
                </div>
                
                <div className="text-left">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#9B8FC1]/10 text-[#9B8FC1] text-sm font-medium rounded-full mb-6">
                    <span className="w-2 h-2 bg-[#9B8FC1] rounded-full"></span>
                    Long-term Success
                  </div>
                  <h3 className="text-3xl font-bold text-neutral-900 mb-8">
                    Stop the cycle of mismatched hires
                  </h3>
                  <div className="space-y-5 text-lg text-neutral-600 leading-relaxed">
                    <p>
                      Even when the credentials look perfect, mismatched expectations lead to early exits and disengagement. With Nomera Health, both sides define what matters most — <strong className="text-[#9B8FC1]">schedule, role type, career goals</strong> — ensuring every match is built on mutual interest, not guesswork.
                    </p>
                    <p>
                      The result? Teams that perform better from day one. By connecting you directly with qualified candidates who want the role, Nomera Health helps reduce the costly churn caused by poor fit and rushed placements.
                    </p>
                  </div>
                </div>
                
                {/* Desktop: Image on right */}
                <div className="hidden lg:flex justify-center">
                  <img 
                    src="/materials/cycle-of-mismatch-profile-image.png" 
                    alt="Cycle of mismatched hires illustration"
                    className="w-80 h-80 md:w-96 md:h-96 lg:w-[500px] lg:h-[500px] object-contain drop-shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features & Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              Why Choose Nomera Health?
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Advanced technology and personalized service to transform your healthcare recruitment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
            {/* AI-Powered Matching */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="w-12 h-12 bg-[#9B8FC1] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-4">AI-Powered Matching</h3>
              <p className="text-neutral-600 mb-6 leading-relaxed">
                Our advanced algorithm matches candidates based on skills, experience, and location preferences.
              </p>
              <ul className="text-sm text-neutral-500 space-y-2">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#9B8FC1] rounded-full"></div>
                  Skills-based matching
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#9B8FC1] rounded-full"></div>
                  Experience level alignment
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#9B8FC1] rounded-full"></div>
                  Geographic preferences
                </li>
              </ul>
            </div>



            {/* Fast Turnaround */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="w-12 h-12 bg-[#9B8FC1] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-4">Fast Turnaround</h3>
              <p className="text-neutral-600 mb-6 leading-relaxed">
                Get qualified candidates quickly, reducing time-to-hire and minimizing staffing gaps.
              </p>
              <ul className="text-sm text-neutral-500 space-y-2">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#9B8FC1] rounded-full"></div>
                  Rapid candidate delivery
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#9B8FC1] rounded-full"></div>
                  Reduced time-to-hire
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#9B8FC1] rounded-full"></div>
                  Minimized staffing gaps
                </li>
              </ul>
            </div>



            {/* Compliance & Safety */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="w-12 h-12 bg-[#9B8FC1] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-4">Compliance & Safety</h3>
              <p className="text-neutral-600 mb-6 leading-relaxed">
                Full compliance with healthcare regulations, including HIPAA and state-specific requirements.
              </p>
              <ul className="text-sm text-neutral-500 space-y-2">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#9B8FC1] rounded-full"></div>
                  HIPAA compliance
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#9B8FC1] rounded-full"></div>
                  Healthcare safety standards
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#9B8FC1] rounded-full"></div>
                  State-specific regulations
                </li>
              </ul>
            </div>

            {/* Cost Efficiency */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
              <div className="w-12 h-12 bg-[#A3CFA2] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-4">Cost Efficiency</h3>
              <p className="text-neutral-600 mb-6 leading-relaxed">
                Reduce recruitment costs with our streamlined process and competitive pricing structure.
              </p>
              <ul className="text-sm text-neutral-500 space-y-2">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#A3CFA2] rounded-full"></div>
                  Reduced recruitment costs
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#A3CFA2] rounded-full"></div>
                  Competitive pricing
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#A3CFA2] rounded-full"></div>
                  ROI-focused solutions
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Three simple steps to find qualified healthcare professionals
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {/* Step 1 */}
            <div className="text-center flex flex-col items-center">
              <div className="w-84 h-84 mb-8 overflow-hidden flex items-center justify-center">
                <img 
                  src="/materials/add-position-image.png" 
                  alt="Add position illustration" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-3">1. Post Your Position</h3>
              <p className="text-neutral-600 max-w-xs mx-auto leading-relaxed">
                Create detailed job postings with your specific requirements, pay rates, and schedule preferences.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center flex flex-col items-center">
              <div className="w-84 h-84 mb-8 overflow-hidden flex items-center justify-center">
                <img 
                  src="/materials/matched-candidates-image.png" 
                  alt="Matched candidates illustration" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-3">2. Review Matches</h3>
              <p className="text-neutral-600 max-w-xs mx-auto leading-relaxed">
                Our AI matches qualified candidates to your positions based on skills, experience, and preferences.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center flex flex-col items-center">
              <div className="w-84 h-84 mb-8 overflow-hidden flex items-center justify-center">
                <img 
                  src="/materials/shaking-hands-interview-image-two.png" 
                  alt="Interview and hire illustration" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-3">3. Interview & Hire</h3>
              <p className="text-neutral-600 max-w-xs mx-auto leading-relaxed">
                Schedule interviews, coordinate with candidates, and make your hiring decision quickly.
              </p>
            </div>
          </div>
        </div>
      </section>



      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
              What Healthcare Employers Say
            </h2>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Hear from healthcare facilities that have transformed their recruitment with Nomera Health
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#9B8FC1] rounded-full flex items-center justify-center mr-3">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-900">Sarah Johnson</h4>
                  <p className="text-sm text-neutral-600">HR Director, Midwest Medical Center</p>
                </div>
              </div>
              <p className="text-neutral-700 italic">
                "Nomera Health revolutionized our hiring process. We went from 6-week hiring cycles to just 10 days. The quality of candidates is exceptional."
              </p>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#A3CFA2] rounded-full flex items-center justify-center mr-3">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-900">Dr. Michael Chen</h4>
                  <p className="text-sm text-neutral-600">Medical Director, Regional Hospital</p>
                </div>
              </div>
              <p className="text-neutral-700 italic">
                "The AI matching is incredibly accurate. We've found candidates who not only meet our technical requirements but also fit our culture perfectly."
              </p>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-100">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-[#9B8FC1] rounded-full flex items-center justify-center mr-3">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-900">Lisa Rodriguez</h4>
                  <p className="text-sm text-neutral-600">Nursing Manager, Community Clinic</p>
                </div>
              </div>
              <p className="text-neutral-700 italic">
                "The support team is amazing. They understand healthcare staffing challenges and always find solutions that work for both us and our candidates."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#9B8FC1]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main White Container */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-[#9B8FC1]/80 px-4 lg:px-8 py-6 text-center">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2">
                Frequently Asked Questions
              </h2>
              <p className="text-xs md:text-sm text-white/90 px-2">
                These questions will be made public. Please don't submit sensitive information.
              </p>
            </div>

            {/* Interactive Content */}
            <div className="flex flex-col lg:flex-row min-h-[500px]">
              {/* Left Side - Questions List */}
              <div className="w-full lg:w-1/2 border-b lg:border-b-0 lg:border-r border-gray-200">
                {/* Search Bar */}
                <div className="p-4 lg:p-6 border-b border-gray-100">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search FAQs..."
                      className="w-full px-4 py-3 pl-10 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#9B8FC1] focus:border-[#9B8FC1] transition-all duration-300"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                  </div>
                </div>

                {/* Questions List */}
                <div className="p-4 space-y-2 max-h-[300px] lg:max-h-[400px] overflow-y-auto">
                  {filteredFAQs.map((faq, index) => (
                    <button
                      key={index}
                      onClick={() => selectFAQ(index)}
                      className={`w-full text-left p-4 rounded-lg transition-all duration-200 ${
                        selectedFAQ === index 
                          ? 'bg-[#9B8FC1] text-white' 
                          : 'hover:bg-gray-50 text-neutral-700'
                      }`}
                    >
                      <h3 className="text-sm font-medium leading-snug">
                        {faq.question}
                      </h3>
                    </button>
                  ))}
                  
                  {/* Submit Question Button */}
                  <button
                    onClick={showForm}
                    className={`w-full text-left p-4 rounded-lg transition-all duration-200 border-2 border-dashed ${
                      showSubmitForm 
                        ? 'border-[#9B8FC1] bg-[#9B8FC1]/5 text-[#9B8FC1]' 
                        : 'border-gray-300 hover:border-[#9B8FC1] text-neutral-600'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      <h3 className="text-sm font-medium">
                        Submit a new question
                      </h3>
                    </div>
                    <p className="text-xs mt-1 opacity-70">
                      Can't find what you're looking for?
                    </p>
                  </button>
                </div>
              </div>

              {/* Right Side - Answer/Form Display */}
              <div className="w-full lg:w-1/2 p-4 lg:p-8">
                {selectedFAQ !== null && !showSubmitForm && (
                  <div className="h-full flex flex-col">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                      {filteredFAQs[selectedFAQ].question}
                    </h3>
                    <div className="flex-1">
                      <p className="text-neutral-600 leading-relaxed">
                        {filteredFAQs[selectedFAQ].answer}
                      </p>
                    </div>
                  </div>
                )}

                {showSubmitForm && (
                  <div className="h-full flex flex-col">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-4">
                      Submit Your Question
                    </h3>
                    
                    <form onSubmit={handleSubmitQuestion} className="flex-1 space-y-4">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-2">
                            Name (Optional)
                          </label>
                          <input
                            type="text"
                            value={questionForm.name}
                            onChange={(e) => setQuestionForm(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#9B8FC1] focus:border-transparent"
                            placeholder="Your name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-2">
                            Contact Method (Optional)
                          </label>
                          <input
                            type="text"
                            value={questionForm.contact}
                            onChange={(e) => setQuestionForm(prev => ({ ...prev, contact: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#9B8FC1] focus:border-transparent"
                            placeholder="Email or phone number"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-neutral-700 mb-2">
                            Your Question *
                          </label>
                          <textarea
                            value={questionForm.question}
                            onChange={(e) => setQuestionForm(prev => ({ ...prev, question: e.target.value }))}
                            required
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#9B8FC1] focus:border-transparent resize-none"
                            placeholder="Type your question here..."
                          />
                        </div>
                      </div>
                      
                      <div className="pt-4">
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full bg-[#9B8FC1] text-white font-semibold py-3 px-6 rounded-lg hover:bg-[#8A7DB0] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? 'Submitting...' : 'Submit Question'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {selectedFAQ === null && !showSubmitForm && (
                  <div className="h-full flex items-center justify-center text-center py-8">
                    <div>
                      <MessageCircle className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                      <p className="text-neutral-500">
                        Select a question to see the answer
                      </p>
                      <p className="text-sm text-neutral-400 mt-2">
                        Or submit a new question if you can't find what you're looking for
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#9B8FC1] relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Stop wasting months and money on staffing gaps.
          </h2>
          <p className="text-lg text-white/80 mb-6 max-w-3xl mx-auto">
            Traditional job boards take weeks. Staffing agencies take 15–20% of your budget. Nomera Health gives you direct access to licensed, ready-to-work candidates in days.
          </p>
          
          {/* Trust indicators */}
          <div className="flex flex-wrap justify-center items-center gap-8 mb-10 text-white/90">
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
              <span className="text-sm font-medium">No setup fees</span>
            </div>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
              <span className="text-sm font-medium">Quality candidates</span>
            </div>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <CheckCircle className="w-5 h-5 text-white flex-shrink-0" />
              <span className="text-sm font-medium">Dedicated support</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Button
              size="lg"
              disabled
              className="bg-white/40 text-white/80 font-bold text-lg px-10 py-5 rounded-xl pointer-events-none select-none"
            >
              Coming soon
              <ArrowRight className="ml-3 h-6 w-6" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              disabled
              className="bg-transparent border-2 border-white/40 text-white/70 font-semibold text-lg px-8 py-5 rounded-xl pointer-events-none select-none"
            >
              Coming soon
            </Button>
          </div>
          
          <p className="text-sm text-white/70 mt-6">
            Get started in minutes • No long-term contracts • Cancel anytime
          </p>
        </div>
      </section>

      <Footer />
      
      {/* Partner showcase styles */}
      <style>{`
        .partner-showcase {
          position: relative;
          padding: 1rem 0;
        }
        
        .partner-logos-container {
          flex: 1;
          overflow: hidden;
          margin: 0 2rem;
        }
        
        .partner-logos-grid {
          display: grid;
          grid-template-columns: repeat(10, 1fr);
          gap: 1.5rem;
          align-items: center;
          justify-items: center;
        }
        
        .partner-nav-btn {
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 50%;
          width: 3rem;
          height: 3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #6B7280;
          flex-shrink: 0;
        }
        
        .partner-nav-btn:hover {
          background: white;
          border-color: rgba(0, 0, 0, 0.2);
          transform: scale(1.05);
          color: #374151;
        }
        
        .partner-logo {
          height: clamp(48px, 12vw, 120px);
          width: auto;
          opacity: 1;
          filter: none;
          transition: transform .2s ease;
          object-fit: contain;
          min-width: clamp(48px, 12vw, 120px);
          display: block;
        }
        
        .partner-logo:hover {
          transform: translateY(-2px);
        }
        
        @media (max-width: 768px) {
          .partner-logos-grid {
            grid-template-columns: repeat(5, 1fr);
            gap: 1rem;
          }
          .partner-nav-btn {
            width: 2.5rem;
            height: 2.5rem;
          }
          .partner-logos-container {
            margin: 0 1rem;
          }
          .partner-logo {
            height: clamp(56px, 15vw, 80px);
            min-width: clamp(56px, 15vw, 80px);
          }
        }
        
        @media (max-width: 480px) {
          .partner-logos-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 0.75rem;
          }
          .partner-nav-btn {
            width: 2rem;
            height: 2rem;
          }
          .partner-logos-container {
            margin: 0 0.5rem;
          }
          .partner-logo {
            height: clamp(64px, 18vw, 88px);
            min-width: clamp(64px, 18vw, 88px);
          }
        }

        /* Mobile carousel styles */
        .partner-logos-container-mobile {
          overflow: hidden;
          position: relative;
          width: 100%;
          height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .partner-logos-carousel {
          display: flex;
          transition: transform 0.3s ease-in-out;
          width: 1000%; /* 10 logos × 100% */
        }

        .partner-logo-mobile {
          flex-shrink: 0;
          width: 100%; /* Each logo takes full container width */
          height: 120px;
          object-fit: contain;
          padding: 0 2rem;
        }

        @media (max-width: 480px) {
          .partner-logos-container-mobile {
            height: 140px;
          }
          .partner-logo-mobile {
            height: 100px;
            padding: 0 1rem;
          }
        }
      `}</style>
    </div>
  );
}
